import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormComponent } from '../form/form.component';
import type { NgxForm } from '../form/ngx-form.types';
import { OverlayService } from '../../services/overlay.service';
import { RagDatasupplyFrontendService, RagDatasupplyTypes } from '@reflact/rag-datasupply';
import { AllUserType, FullUser, Vkc } from '@reflact/reflactportal';
import { FaqService } from '../../services/faq.service';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  providers: [RagDatasupplyFrontendService],
  standalone: false
})
export class UserManagementComponent<T extends AllUserType> implements AfterViewInit, OnInit {
  @Input({ required: true }) public service!: RagDatasupplyFrontendService<T>;
  @Input({ required: true }) public columns!: RagDatasupplyTypes.TableColumn[];
  @Input({ required: true }) public formFields!: NgxForm.FormField[];
  @Input({ required: true }) public formFieldsCreator!: NgxForm.FormField[];
  @Input({ required: true }) public formFieldCreatorData!: Object;
  @Input({ required: true }) public newUserText!: string;
  @Input({ required: true }) public deleteButton!: boolean;
  @Input({ required: true }) public translatedLabels!: NgxForm.TranslatedLabels
  @Input() public enableVkcs: boolean = false;
  /** Optionaler Callback des Elternteils fürs eigentliche Speichern — liefert zurück, ob es
   * erfolgreich war, damit der Speichern-Button (statt eines Toasters) kurz Feedback geben kann,
   * siehe flashSaveFeedback(). Ohne diesen Input fällt updateUser() auf das alte
   * updateUserEvent-Verhalten zurück. */
  @Input() public saveUser?: (data: T) => Promise<boolean>;
  /** Vom Elternteil gesetzt, solange ein neuer Nutzer angelegt wird (Ladezustand am Speichern-Button). */
  @Input() public creating = false;
  public saving = false;
  @ViewChild("excludeCrmIds") public excludeCrmIds: any;
  /** Refs to the embedded forms so the Speichern/Löschen buttons can live in the Drawer-Footer
   * statt im mitscrollenden Formularinhalt. */
  @ViewChild("form") public editFormRef?: FormComponent;
  @ViewChild("createForm") public createFormRef?: FormComponent;
  @Output() public updateUserEvent = new EventEmitter<T>();
  @Output() public createUserEvent = new EventEmitter<T>();
  @Output() public deleteUserEvent = new EventEmitter<T>();
  @Output() public selectUserEvent = new EventEmitter<T>();
  public vkcs: Vkc[] = [];
  /** Zeilenmarkierung in der p-table — unabhängig vom Bearbeiten-Drawer, siehe closeEditUser(). */
  public selectedUser?: T;
  /** Steuert den Bearbeiten-Drawer (siehe selectUserForEdit()/closeEditUser()). */
  public editVisible = false;
  public editUser?: T;
  /** Steuert den "Neuer Nutzer"-Drawer (siehe open()/closeCreateUser()). */
  public createVisible = false;

  constructor(private overlayService: OverlayService, private faqService: FaqService, private cdr: ChangeDetectorRef) {
  }


  ngOnInit(): void {
    if (this.enableVkcs) {
      this.faqService.getAccountInfo().then(() => {
        this.vkcs = this.faqService.serviceVkcs;
        if (this.vkcs && this.vkcs.length > 1) {
          this.formFields.push({
            fieldName: 'excludeCrmIds', type: 'template', options: {
              template: this.excludeCrmIds
            }
          });
        }
      })
    }
  }

  ngAfterViewInit(): void {
    this.service.results$.subscribe(r => {
      const selectedId = this.overlayService.get("selected");
      if (!r || r.length === 0 || !selectedId) return;
      const match = r.find(u => u._id.toString() == selectedId);
      if (!match) {
        void this.closeEditUser();
        return;
      }
      // Drawer für diesen Nutzer bereits offen (z.B. Refetch nach Speichern) — nicht mit einem
      // frisch nachgeladenen Stand überschreiben, siehe selectUserForEdit().
      if (this.editVisible && this.editUser?._id === match._id) {
        return;
      }
      this.editUser = { ...match };
      this.editUserSnapshot = JSON.stringify(this.editUser);
      this.editVisible = true;
      this.saveFeedback = 'idle';
      // Emittiert die Kopie, nicht `match` — sonst reichert onSelectGlobalUser() (Parent) das
      // FALSCHE Objekt mit mandants/mandantDetails an, nicht das tatsächlich angezeigte editUser.
      this.selectUserEvent.emit(this.editUser);
    })
  }

  /** Spaltenbeschriftung der p-table — Priorität: translatedLabels, dann column.label, dann fieldName. */
  public getColumnLabel(column: RagDatasupplyTypes.TableColumn): string {
    const translated = this.translatedLabels.get(column.fieldName);
    if (translated) return translated;
    if (column.label || column.label == '') return column.label;
    return column.fieldName;
  }

  public async updateUser(event: any) {
    this.editUserSnapshot = JSON.stringify(event);
    if (this.saveUser) {
      // Feedback direkt am Speichern-Button statt Toastr — der Elternteil meldet hier zurück,
      // ob der Request tatsächlich erfolgreich war.
      const success = await this.runSaving(() => this.saveUser!(event));
      if (success) {
        this.flashSaveFeedback();
      }
      return;
    }
    this.updateUserEvent.emit(event)
  }

  /** Kurzes Erfolgs-Feedback direkt am Speichern-Button (grün + Häkchen), siehe saveUser(). */
  public saveFeedback: 'idle' | 'saved' = 'idle';

  private async runSaving<R>(action: () => Promise<R>): Promise<R> {
    this.saving = true;
    this.cdr.detectChanges();
    try {
      return await action();
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }

  private flashSaveFeedback(): void {
    this.saveFeedback = 'saved';
    this.cdr.detectChanges();
    setTimeout(() => {
      this.saveFeedback = 'idle';
      this.cdr.detectChanges();
    }, 1500);
  }

  /** Vergleicht `editUser` mit dem beim Öffnen/letzten Speichern erfassten Stand — der
   * Speichern-Button soll erst aktiv werden, wenn tatsächlich etwas geändert wurde. */
  private editUserSnapshot?: string;

  public hasUserChanges(): boolean {
    return !!this.editUser && JSON.stringify(this.editUser) !== this.editUserSnapshot;
  }

  /** Für Aufrufer, die `editUser` nach dem Öffnen noch asynchron mit weiteren Feldern anreichern
   * (z.B. global-user-administration#onSelectGlobalUser) — friert den Dirty-Vergleichsstand neu
   * ein, sonst zeigt "Speichern" sofort als geändert, obwohl der Nutzer noch nichts gemacht hat. */
  public refreshEditUserSnapshot(): void {
    this.editUserSnapshot = JSON.stringify(this.editUser);
  }

  public createUser(event: any) {
    this.createUserEvent.emit(event);
  }

  public deleteUser(event: any) {
    this.deleteUserEvent.emit(event);
  }

  /** Öffnet einen Nutzer in der Bearbeiten-Ansicht (Klick auf Tabellenzeile) — bearbeitet bewusst
   * eine Kopie, nicht die Tabellenzeile direkt: "Abbrechen"/anderweitiges Schließen des Drawers
   * muss unsaved Änderungen verwerfen können, siehe closeEditUser(). */
  public selectUserForEdit(user: T): void {
    this.editUser = { ...user };
    this.editUserSnapshot = JSON.stringify(this.editUser);
    this.editVisible = true;
    this.saveFeedback = 'idle';
    this.overlayService.set('selected', user._id.toString());
    // Emittiert die Kopie, nicht `user` — siehe ngAfterViewInit() für denselben Grund.
    this.selectUserEvent.emit(this.editUser);
  }

  /** Schließt den Bearbeiten-Drawer (Abbruch, Löschen oder Deep-Link ohne Treffer). Awaited, damit
   * ein direkt danach folgender Listen-Refresh nicht auf einen noch nicht geleerten
   * `selected`-Query-Param trifft und den Drawer sofort wieder öffnet (siehe ngAfterViewInit()). */
  public async closeEditUser(): Promise<void> {
    this.editVisible = false;
    this.editUser = undefined;
    this.selectedUser = undefined;
    this.saveFeedback = 'idle';
    await this.overlayService.set('selected', undefined);
  }

  public open() {
    this.createVisible = true;
  }

  /** Schließt den "Neuer Nutzer"-Drawer (Abbruch). */
  public closeCreateUser(): void {
    this.createVisible = false;
  }

  public onToggleVkc(data: FullUser, crmId: string) {
    const index = data.excludeCrmIds.indexOf(crmId);
    if (index >= 0) {
      data.excludeCrmIds.splice(index, 1);
    } else {
      data.excludeCrmIds.push(crmId);
    }
    this.updateUserEvent.emit(data as any);
  }
}
